<template>
  <div
    class="product__gallery"
    :class="{ 'initial-image-png' : hasColouredBgOption }"
  >
    <div class="product__gallery-inner">
      <ProductSaleBadge />

      <vue-flickity
        :key="`thumbs-${product.id}`"
        ref="thumbnailCarousel"
        class="product__gallery-thumbnails"
        :options="{
          prevNextButtons: false,
          pageDots: false,
          setGallerySize: false,
          contain: true,
          cellSelector: '.product__gallery-thumbnail-wrapper',
          cellAlign: 'left',
          wrapAround: true,
          draggable: false
        }"
      >
        <div
          v-for="(image, index) in thumbnailImages"
          :key="`thumbnail-${image}`"
          class="product__gallery-thumbnail-wrapper"
        >
          <div
            class="product__gallery-thumbnail"
            :class="{ 'product__gallery-thumbnail--png': image.includes('.png') }"
            @click="goToSlide(index)"
          >
            <img
              :src="image"
              :alt="`thumbnail-image-${index}`"
              width="123"
              height="123"
            >
          </div>
        </div>

        <button
          class="product__gallery-button product__gallery-button--prev"
          :class="{ 'product__gallery-button--disabled': atFirstSlide }"
          :disabled="atFirstSlide"
          aria-label="Previous Slide"
          @click="previousSlide"
        >
          <ArrowLeft />
        </button>
        <button
          class="product__gallery-button product__gallery-button--next"
          :class="{ 'product__gallery-button--disabled': atLastSlide }"
          :disabled="atLastSlide"
          aria-label="Next Slide"
          @click="nextSlide"
        >
          <ArrowLeft />
        </button>
      </vue-flickity>


      <vue-flickity
        :key="`main-${product.id}`"
        ref="mainCarousel"
        class="product__gallery-main-image-slider"
        :options="{
          prevNextButtons: false,
          pageDots: true,
          lazyLoad: 2,
          cellAlign: 'left',
          wrapAround: true,
        }"
      >
        <div
          v-for="(image, index) in largeMedia"
          :key="`main-image-${image}`"
          class="product__gallery-main-image"
          :data-elite-points="index === 0 ? pointsText : null"
          :data-promo-badge="index === 0 ? promoBadge : null"
        >
          <template v-if="index === 0">
            <img
              v-if="image.media_type === 'image'"
              :src="image.preview_image.src"
              :alt="`image-${index}`"
              width="500"
              height="500"
              loading="eager"
              class="hidden md:block"
            >
            <img
              v-if="image.media_type === 'image'"
              :src="image.preview_image.mobileSrc"
              :alt="`image-${index}`"
              width="288"
              height="271"
              loading="eager"
              class="md:hidden"
            >
          </template>
          <template v-else>
            <img
              v-if="image.media_type === 'image'"
              :data-flickity-lazyload="image.preview_image.src"
              :alt="`image-${index}`"
              width="500"
              height="500"
              class="hidden md:block"
            >
            <img
              v-if="image.media_type === 'image'"
              :src="image.preview_image.mobileSrc"
              :alt="`image-${index}`"
              width="288"
              height="271"
              class="md:hidden"
            >
          </template>

          <video
            v-if="image.media_type === 'video'"
            width="500"
            height="500"
            controls
          >
            <source
              v-for="source in image.sources"
              :key="source.format"
              :src="source.url"
              :type="source.mime_type"
            >
            Your browser does not support the video tag.
          </video>
        </div>
      </vue-flickity>
    </div>
  </div>
</template>


<script>
import { mapState } from 'pinia'
import { useProductStore } from '../../pinia/ProductStore'
import {productPointsValue} from '../../helpers/product'

import ProductSaleBadge from './product-sale-badge'
import ArrowLeft from '../../icons/arrow-left'

export default {
  components: {
    ArrowLeft,
    ProductSaleBadge
  },
  props: {
  },
  data () {
    return {
      atFirstSlide: true,
      atLastSlide: false,
      currentSlideIndex: 0
    }
  },
  computed: {
    ...mapState (useProductStore, [
      'product',
      'gradientBackgroundColours',
      'hasColouredBgOption',
      'thumbnailImages',
      'largeMedia'
    ]),
    gallerySize () {
      return this.largeMedia.length
    },
    promoBadge () {
      const badgeTag = this.product.tags.find(tag => tag.includes('promo-badge:'))
      if (badgeTag) {
        return badgeTag.split(':')[1]
      }
      return null
    },
    pointsText() {
      return productPointsValue(this.product.price)
    }
  },
  watch: {
    async product () {
      await this.$refs.thumbnailCarousel.select(0)
      await this.$refs.mainCarousel.select(0)
      this.currentSlideIndex = 0
      this.atFirstSlide = true
      this.atLastSlide = false
      this.galleryLoad()
    },
  },
  mounted () {
    this.galleryLoad()
  },
  methods: {
    galleryLoad () {
      this.$el.classList.remove('product__gallery--loading')
      this.$refs.thumbnailCarousel.reloadCells()
      this.galleryDragWatcher()
    },
    refreshSliderPositions (newSlideIndex) {
      this.atFirstSlide = newSlideIndex === 0
      this.atLastSlide = newSlideIndex === (this.gallerySize - 1)
      this.currentSlideIndex = newSlideIndex
    },
    galleryDragWatcher () {
      this.$refs.mainCarousel.on('change', newSlideIndex => {
        this.$refs.thumbnailCarousel.select(newSlideIndex)
        this.$refs.thumbnailCarousel.reloadCells()

        this.refreshSliderPositions(newSlideIndex)
      })
    },
    previousSlide () {
      this.$refs.thumbnailCarousel.previous()
      this.$refs.mainCarousel.previous()
    },
    nextSlide () {
      this.$refs.thumbnailCarousel.next()
      this.$refs.mainCarousel.next()
    },
    goToSlide (index) {
      this.$refs.thumbnailCarousel.select(index)
      this.$refs.thumbnailCarousel.reloadCells()
      this.$refs.mainCarousel.select(index)
      this.$refs.mainCarousel.reloadCells()
      this.refreshSliderPositions(index)
    }
  }
}
</script>
